import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import type {
  CharacterCreationChoiceNodeDnd5e,
  CharacterCreationChoiceValue,
  CharacterCreationDraftDnd5e,
} from "@/lib/core/schemas/character-creation";
import type { Dnd5eAbilityScoreTag } from "@/lib/rules/dnd5e/vocabulary/ability-score";
import { createAccountCharacter } from "@/src/api/character-creation";
import { BackgroundStep } from "@/src/components/wizard/background-step";
import { CharacterCreationGraphStep } from "@/src/components/wizard/character-creation-graph-step";
import { CharacterStep } from "@/src/components/wizard/character-step";
import { CharacterReviewStep } from "@/src/components/wizard/review/character-review-step";
import { SpeciesStep } from "@/src/components/wizard/species-step";
import type { WizardStep } from "@/src/components/wizard/wizard-state";
import { WizardStepper } from "@/src/components/wizard/wizard-stepper";
import { useStandaloneCharacterCreationEvaluation } from "@/src/hooks/use-character-creation-evaluation";

const STANDALONE_STEPS: readonly { key: WizardStep; label: string }[] = [
  { key: "species", label: "Lineage" },
  { key: "character", label: "Character" },
  { key: "background", label: "Origin" },
  { key: "graph", label: "Choices" },
  { key: "review", label: "Review" },
];

const makeCreationId = (): string => {
  if (typeof globalThis.crypto?.randomUUID === "function") {
    return globalThis.crypto.randomUUID();
  }

  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (part) => {
    const randomNibble = Math.floor(Math.random() * 16);
    const value = part === "x" ? randomNibble : (randomNibble & 0x3) | 0x8;
    return value.toString(16);
  });
};

const sortedForCompare = (value: unknown): unknown => {
  if (Array.isArray(value)) {
    return value.map(sortedForCompare);
  }

  if (value && typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value)
        .sort(([left], [right]) => left.localeCompare(right))
        .map(([key, nestedValue]) => [key, sortedForCompare(nestedValue)])
    );
  }

  return value;
};

const draftsEqual = (
  left: CharacterCreationDraftDnd5e,
  right: CharacterCreationDraftDnd5e
): boolean =>
  JSON.stringify(sortedForCompare(left)) ===
  JSON.stringify(sortedForCompare(right));

const sameAbilityScores = (
  left: CharacterCreationDraftDnd5e["abilityScores"],
  right: CharacterCreationDraftDnd5e["abilityScores"]
): boolean => {
  if (!left || !right) {
    return left === right;
  }

  return (
    (Object.keys(left) as Dnd5eAbilityScoreTag[]).length ===
      (Object.keys(right) as Dnd5eAbilityScoreTag[]).length &&
    (Object.keys(left) as Dnd5eAbilityScoreTag[]).every(
      (ability) => left[ability] === right[ability]
    )
  );
};

const makeDraft = (): CharacterCreationDraftDnd5e => ({
  creationId: makeCreationId(),
  choices: {},
});

export default function StandaloneCharacterCreationPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState<WizardStep>("species");
  const [draft, setDraft] = useState<CharacterCreationDraftDnd5e>(makeDraft);
  const [focusedGraphNodeId, setFocusedGraphNodeId] = useState<string | null>(
    null
  );
  const [confirmError, setConfirmError] = useState<Error | null>(null);
  const [isConfirming, setIsConfirming] = useState(false);
  const {
    evaluation,
    invalidations,
    isLoading: isEvaluationLoading,
    error: evaluationError,
  } = useStandaloneCharacterCreationEvaluation(
    step === "graph" || step === "review",
    draft
  );

  useEffect(() => {
    if (!evaluation) {
      return;
    }
    setDraft((current) => {
      if (draftsEqual(current, evaluation.normalizedDraft)) {
        return current;
      }
      return evaluation.normalizedDraft;
    });
  }, [evaluation]);

  const updateDraft = useCallback(
    (
      updater: (
        current: CharacterCreationDraftDnd5e
      ) => CharacterCreationDraftDnd5e
    ) => {
      setConfirmError(null);
      setDraft((current) => updater(current));
    },
    []
  );

  const handleGraphSelection = useCallback(
    (
      node: CharacterCreationChoiceNodeDnd5e,
      value: CharacterCreationChoiceValue
    ) => {
      updateDraft((current) => ({
        ...current,
        choices: { ...current.choices, [node.id]: value },
      }));
      setFocusedGraphNodeId(null);
    },
    [updateDraft]
  );

  const handleBack = useCallback(() => {
    setStep((current) => {
      if (current === "character") {
        return "species";
      }
      if (current === "background") {
        return "character";
      }
      if (current === "graph") {
        return "background";
      }
      if (current === "review") {
        return "graph";
      }
      return current;
    });
  }, []);

  const handleChangeReviewNode = useCallback((nodeId: string) => {
    setConfirmError(null);
    if (nodeId === "base:species") {
      setStep("species");
      return;
    }
    if (nodeId === "base:class" || nodeId === "base:abilities") {
      setStep("character");
      return;
    }
    if (nodeId === "base:background") {
      setStep("background");
      return;
    }
    setFocusedGraphNodeId(nodeId);
    setStep("graph");
  }, []);

  const handleConfirm = useCallback(async () => {
    if (
      isConfirming ||
      evaluation?.status !== "complete" ||
      !evaluation.preview
    ) {
      return;
    }
    const planDigest = evaluation.preview.planDigest;
    setConfirmError(null);
    setIsConfirming(true);
    try {
      const result = await createAccountCharacter(
        evaluation.normalizedDraft,
        planDigest
      );
      navigate(
        `/launch?characterId=${encodeURIComponent(result.character.id)}`,
        {
          replace: true,
        }
      );
    } catch (caught) {
      setConfirmError(
        caught instanceof Error ? caught : new Error(String(caught))
      );
      setIsConfirming(false);
    }
  }, [evaluation, isConfirming, navigate]);

  return (
    <div className="flex h-dvh flex-col bg-background text-foreground">
      <WizardStepper currentStep={step} steps={STANDALONE_STEPS} />
      {step === "species" && (
        <SpeciesStep
          onNext={(selection) => {
            updateDraft((current) => ({
              ...current,
              speciesId: selection.speciesId,
              choices:
                current.speciesId === selection.speciesId
                  ? current.choices
                  : {},
            }));
            setStep("character");
          }}
        />
      )}
      {step === "character" && (
        <CharacterStep
          onBack={() => setStep("species")}
          onNext={(config) => {
            updateDraft((current) => ({
              ...current,
              name: config.name,
              className: config.class,
              abilityScores: config.abilityScores,
              choices:
                current.className === config.class &&
                sameAbilityScores(current.abilityScores, config.abilityScores)
                  ? current.choices
                  : {},
            }));
            setStep("background");
          }}
        />
      )}
      {step === "background" && (
        <BackgroundStep
          onBack={() => setStep("character")}
          onNext={(selection) => {
            updateDraft((current) => ({
              ...current,
              backgroundId: selection.background,
              choices:
                current.backgroundId === selection.background
                  ? current.choices
                  : {},
            }));
            setStep("graph");
          }}
          onSkip={() => {
            updateDraft((current) => ({
              ...current,
              backgroundId: null,
              choices: current.backgroundId === null ? current.choices : {},
            }));
            setStep("graph");
          }}
        />
      )}
      {step === "graph" && (
        <CharacterCreationGraphStep
          error={evaluationError}
          evaluation={evaluation}
          focusNodeId={focusedGraphNodeId}
          invalidations={invalidations}
          isLoading={isEvaluationLoading}
          onBack={handleBack}
          onNext={() => setStep("review")}
          onSelect={handleGraphSelection}
        />
      )}
      {step === "review" && evaluation?.status === "complete" && (
        <>
          {confirmError && (
            <div className="border-destructive border-b bg-destructive/10 px-4 py-2 text-center text-destructive text-sm">
              {confirmError.message}
            </div>
          )}
          <CharacterReviewStep
            confirmDisabled={isConfirming}
            confirmLabel={isConfirming ? "Saving..." : "Create"}
            evaluation={evaluation}
            onBack={() => setStep("graph")}
            onChangeNode={handleChangeReviewNode}
            onConfirm={handleConfirm}
          />
        </>
      )}
      {step === "review" && evaluation?.status !== "complete" && (
        <CharacterCreationGraphStep
          error={evaluationError}
          evaluation={evaluation}
          invalidations={invalidations}
          isLoading={isEvaluationLoading}
          onBack={handleBack}
          onNext={() => setStep("review")}
          onSelect={handleGraphSelection}
        />
      )}
    </div>
  );
}
